/* eslint-disable perfectionist/sort-object-types */
import { useCallback, useRef } from 'react';

import type { CustomData } from '../../types';
import {
	type EndStateConfig,
	type ExperiencePerformanceTypes,
	type ExperienceTypes,
	UFOExperience,
} from '../core';

type UFOExperienceProps = {
	id: string;
	component: string;
	type: ExperienceTypes;
	performanceType: ExperiencePerformanceTypes;
};

export const useUFOExperience = ({ id, component, type, performanceType }: UFOExperienceProps) => {
	const ufoExperience = useRef<UFOExperience>(
		new UFOExperience(id, {
			type,
			performanceType,
			platform: { component },
		}),
	);

	const start = useCallback((startTime?: number): Promise<void> => {
		return ufoExperience.current.start(startTime);
	}, []);

	const success = useCallback((config?: EndStateConfig) => {
		return ufoExperience.current.success(config);
	}, []);

	const mark = useCallback((name: string, timestamp?: number): void => {
		ufoExperience.current.mark(name, timestamp);
	}, []);

	const addMetadata = useCallback((data: CustomData): void => {
		ufoExperience.current.addMetadata(data);
	}, []);

	const markFMP = useCallback((timestamp?: number): void => {
		ufoExperience.current.markFMP(timestamp);
	}, []);

	const markInlineResponse = useCallback((timestamp?: number): void => {
		ufoExperience.current.markInlineResponse(timestamp);
	}, []);

	const failure = useCallback((config?: EndStateConfig) => {
		return ufoExperience.current.failure(config);
	}, []);

	const abort = useCallback((config?: EndStateConfig) => {
		return ufoExperience.current.abort(config);
	}, []);

	return { start, success, failure, abort, mark, addMetadata, markFMP, markInlineResponse };
};
